import { AnyEventObject, assign, createMachine } from 'xstate';
import { FetchResult } from '@apollo/client';
import { flow } from 'fp-ts/function';
import { rejectGraphqlError } from 'modules/ai/utils';
import {
  PredictiveGoalsResponse,
  PredictiveAudienceGoal,
  VocabularyResponse,
} from '../../../types';
import { defaultCoreGoalsContext } from './CoreGoalsContext';
import { parseGoalsFromResponse } from '../../../goalUtils';

export const CoreGoalsContextStates = {
  goals: {
    inactive: 'inactive',
    queryGoals: 'queryGoals',
    goalsError: 'goalsError',
    goalsComplete: 'goalsComplete',
    name: 'goals',
  },
  vocab: {
    inactive: 'inactive',
    queryVocab: 'queryVocab',
    vocabError: 'vocabError',
    vocabComplete: 'vocabComplete',
    name: 'vocab',
  },
};

export interface CoreGoalsContextState {
  goals: Array<PredictiveAudienceGoal>;
  vocab: VocabularyResponse;
}
const type = 'parallel';
export const coreGoalsStateMachinePlan = {
  predictableActionArguments: true,
  id: 'coreGoalsStateMachine',
  type: type as 'parallel',
  context: {
    goals: defaultCoreGoalsContext.goals.goalData,
    vocab: defaultCoreGoalsContext.vocab.vocabData,
  },
  states: {
    [CoreGoalsContextStates.goals.name]: {
      initial: CoreGoalsContextStates.goals.inactive,
      states: {
        [CoreGoalsContextStates.goals.inactive]: {
          on: {
            [CoreGoalsContextStates.goals.queryGoals]: {
              target: CoreGoalsContextStates.goals.queryGoals,
            },
          },
        },
        [CoreGoalsContextStates.goals.queryGoals]: {
          invoke: {
            src: 'queryGoals',
            onDone: {
              target: CoreGoalsContextStates.goals.goalsComplete,
              actions: 'handleGoalsComplete',
            },
            onError: {
              target: CoreGoalsContextStates.goals.goalsError,
            },
          },
        },
        [CoreGoalsContextStates.goals.goalsComplete]: {
          on: {
            [CoreGoalsContextStates.goals.queryGoals]: {
              target: [CoreGoalsContextStates.goals.queryGoals],
            },
          },
        },
        [CoreGoalsContextStates.goals.goalsError]: {
          on: {
            [CoreGoalsContextStates.goals.queryGoals]: {
              target: [CoreGoalsContextStates.goals.queryGoals],
            },
          },
        },
      },
    },
    [CoreGoalsContextStates.vocab.name]: {
      initial: CoreGoalsContextStates.vocab.inactive,
      states: {
        [CoreGoalsContextStates.vocab.inactive]: {
          on: {
            [CoreGoalsContextStates.vocab.queryVocab]: {
              target: CoreGoalsContextStates.vocab.queryVocab,
            },
          },
        },
        [CoreGoalsContextStates.vocab.queryVocab]: {
          invoke: {
            src: 'queryVocab',
            onDone: {
              target: CoreGoalsContextStates.vocab.vocabComplete,
              actions: 'handleVocabComplete',
            },
            onError: {
              target: CoreGoalsContextStates.vocab.vocabError,
            },
          },
        },
        [CoreGoalsContextStates.vocab.vocabComplete]: {
          on: {
            [CoreGoalsContextStates.vocab.queryVocab]: {
              target: [CoreGoalsContextStates.vocab.queryVocab],
            },
          },
        },
        [CoreGoalsContextStates.vocab.vocabError]: {
          on: {
            [CoreGoalsContextStates.vocab.queryVocab]: {
              target: [CoreGoalsContextStates.vocab.queryVocab],
            },
          },
        },
      },
    },
  },
};

export interface GoalsResponseEvent extends AnyEventObject {
  data: {
    data: PredictiveGoalsResponse;
  };
}
export interface VocabResponseEvent extends AnyEventObject {
  data: {
    data: VocabularyResponse;
  };
}
export const coreGoalsStateMachine =
  createMachine<CoreGoalsContextState>(coreGoalsStateMachinePlan);

export type CoreGoalStateMachineEffects = {
  queryGoals: () => Promise<FetchResult<PredictiveGoalsResponse>>;
  queryVocab: () => Promise<FetchResult<VocabularyResponse>>;
};
export const createCoreGoalStateMachineEffects = ({
  queryGoals,
  queryVocab,
}: CoreGoalStateMachineEffects) => {
  return {
    actions: {
      handleGoalsComplete: assign((context: CoreGoalsContextState, event: unknown) => {
        return {
          ...context,
          goals: parseGoalsFromResponse((event as GoalsResponseEvent)?.data?.data),
        };
      }),
      handleVocabComplete: assign((context: CoreGoalsContextState, event: unknown) => {
        return {
          ...context,
          vocab: (event as VocabResponseEvent)?.data?.data,
        };
      }),
    },
    services: {
      queryGoals: () => flow(queryGoals, rejectGraphqlError),
      queryVocab: () => flow(queryVocab, rejectGraphqlError),
    },
  };
};
